import axios from 'axios'
import {
  dropDown,
  employeesGrid,
  inactiveEmployee,
  updateEmployeeTypes,
  targetEmployeeTypes,
} from './interfaces'
import GetJsonData from './dataAccess'
import { filter } from '@progress/kendo-data-query/dist/npm/transducers'

const apiURL = process.env.REACT_APP_API_URL

export const getEmployees = async (): Promise<employeesGrid[]> => {
  const data = await GetJsonData('Employee')

  const employeeData: employeesGrid[] = (data as employeesGrid[]).map(
    (item: any) => ({
      employeeId: item.employeeId,
      employeeName: item.employeeName,
      position: item.position,
      subcontractor: item.subcontractor,
      adminRole: item.adminRole,
      department: item.department,
      departmentColourHex: item.departmentColourHex,
    })
  )

  return employeeData
}

export const getEmployeesForDiary = async (contractorOnly: boolean, filterIds?: number[]): Promise<employeesGrid[]> => {
  const data = await GetJsonData(`Employee?contractorOnly=${contractorOnly}`)

  let employeeData: employeesGrid[] = (data as employeesGrid[]).map(
    (item: any) => ({
      employeeId: item.employeeId,
      employeeName: item.employeeName,
      position: item.position,
      subcontractor: item.subcontractor,
      adminRole: item.adminRole,
      department: item.department,
      departmentColourHex: item.departmentColourHex,
    })
  )

  if(filterIds && filterIds.length > 0) {
    employeeData = employeeData.filter(employee => filterIds.includes(employee.employeeId))
  } 

  return employeeData
}

export const updateEmployee = async (
  employee: updateEmployeeTypes,
  employeeId: number
) => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const requestData = {
    email: employee.email,
    permissions: employee.permissions,
    employeeId: employeeId,
    employeeName: employee.employeeName,
    position: employee.position,
    subcontractor: employee.subcontractor,
    password: employee.password,
    adminRole: employee.adminRole,
    sortOrder: employee.sortOrder,
    defaultWorkTypeGroupId: employee.worktypegroupdefaultId,
    defaultWorktypeId: employee.worktypedefaultId,
    hourlyRate: employee.hourlyRate,
    employeeTypeId: employee.employeeTypeId,
    mobilePhone: employee.mobilePhone,
    groupIds: employee.groupIds,
    directReportEmployeeIds: employee.directReportEmployeeIds,
    canViewAllUsers: employee.canViewAllUsers,
  }

  const response = await axios({
    url: `https://${apiURL}/Employee/${employeeId}`,
    method: 'put',
    data: requestData,
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    },
  })

  const data: updateEmployeeTypes = response.data
  return data
}

/**
 * Every active employee as dropdown options, newest name order.
 *
 * Built on getEmployees rather than the ForDropdown endpoint because that one
 * calls axios directly with a token read straight from storage: an expired
 * token comes back 401 and the picker is silently empty. getEmployees goes
 * through the shared instance, which refreshes the token and retries.
 */
export const getEmployeeOptions = async (): Promise<dropDown[]> => {
  const employees = await getEmployees()

  return employees
    .map((e) => ({ text: e.employeeName, value: e.employeeId }))
    .sort((a, b) => a.text.localeCompare(b.text))
}

export const getEmployeeDropdown = async (
  searchTerm: string
): Promise<dropDown[]> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.get(
    `https://${apiURL}/Employee/ForDropdown?filter=${searchTerm}`,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    }
  )

  const data = response.data

  const employeeDropdownData: dropDown[] = (data as dropDown[]).map(
    (item: any) => ({
      text: item.text,
      value: parseInt(item.value),
    })
  )

  return employeeDropdownData
}

export const deleteEmployee = async (employeeId: number) => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.delete(
    `https://${apiURL}/Employee/${employeeId}`,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    }
  )

  const data: any = response.data

  return data
}

export const getTargetEmployee = async (
  employeeId: number
): Promise<targetEmployeeTypes> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.get(`https://${apiURL}/Employee/${employeeId}`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    },
  })

  const data: targetEmployeeTypes = response.data

  const targetSiteData: targetEmployeeTypes = {
    email: data.email,
    permissions: data.permissions,
    employeeId: data.employeeId,
    employeeName: data.employeeName,
    position: data.position,
    subcontractor: data.subcontractor,
    adminRole: data.adminRole,
    sortOrder: data.sortOrder,
    worktypegroupDefaultId: data.worktypegroupDefaultId,
    worktypegroupDefault: data.worktypegroupDefault,
    worktypeDefaultId: data.worktypeDefaultId,
    worktypeDefault: data.worktypeDefault,
    hourlyRate: data.hourlyRate,
    type: data.type,
    employeeTypeId: data.employeeTypeId,
    mobilePhone: data.mobilePhone,
    groups: data.groups || [],
    directReports: data.directReports || [],
    canViewAllUsers: data.canViewAllUsers ?? false,
  }

  return targetSiteData
}

// Get current user's employee data
export const getCurrentUserEmployee = async (): Promise<targetEmployeeTypes | null> => {
  const token = localStorage.getItem('authToken')
  
  if (!token) {
    return null
  }

  try {
    const response = await axios({
      url: `https://${apiURL}/Employee/myinformation`,
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    })

    const data = response.data

    const targetEmployeeData: targetEmployeeTypes = {
      employeeId: data.employeeId,
      employeeName: data.employeeName,
      position: data.position,
      subcontractor: data.subcontractor,
      adminRole: data.adminRole,
      sortOrder: data.sortOrder,
      worktypegroupDefaultId: data.worktypegroupDefaultId,
      worktypegroupDefault: data.worktypegroupDefault,
      worktypeDefaultId: data.worktypeDefaultId,
      worktypeDefault: data.worktypeDefault,
      hourlyRate: data.hourlyRate,
      permissions: data.permissions,
      email: data.email,
      type: data.type,
      employeeTypeId: data.employeeTypeId,
      mobilePhone: data.mobilePhone,
      directReports: data.directReports || [],
      canViewAllUsers: data.canViewAllUsers ?? false,
    }

    return targetEmployeeData
  } catch (error) {
    console.error('Error fetching current user employee data:', error)
    return null
  }
}

export const getAssignedToForJobsFilterDropdown = async (
  searchTerm: string
): Promise<dropDown[]> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.get(
    `https://${apiURL}/Jobs/AssignedTo/ForFilterDropdown?filter=${searchTerm}`,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    }
  )

  const data = response.data

  const assignedToData: dropDown[] = (data as dropDown[]).map(
    (item: any) => ({
      text: item.text,
      value: item.value,
    })
  )

  return assignedToData
}

export const getAssignedEmployeeForJobsFilterDropdown = async (
  searchTerm: string
): Promise<dropDown[]> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.get(
    `https://${apiURL}/Jobs/AssignedEmployee/ForFilterDropdown?filter=${searchTerm}`,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    }
  )

  const data = response.data

  const assignedEmployeeData: dropDown[] = (data as dropDown[]).map(
    (item: any) => ({
      text: item.text,
      value: item.value,
    })
  )

  return assignedEmployeeData
}

export const getEmployeeContacts = async (): Promise<any[]> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.get(
    `https://${apiURL}/Employee/Contacts`,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    }
  )

  const data = response.data

  const contactData = (data as any[]).map((item: any) => ({
    employeeId: item.employeeId,
    employeeName: item.employeeName,
    position: item.position || '',
    email: item.email || '',
    mobilePhone: item.mobilePhone || '',
  }))

  return contactData
}

/** The signed-in user's portal preferences. */
export interface UserPreferences {
  rememberFilters: boolean
}

export const getMyPreferences = async (): Promise<UserPreferences> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios({
    url: `https://${apiURL}/Employee/MyPreferences`,
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    },
  })

  return { rememberFilters: response.data?.rememberFilters ?? true }
}

export const updateMyPreferences = async (
  preferences: UserPreferences
): Promise<UserPreferences> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios({
    url: `https://${apiURL}/Employee/MyPreferences`,
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    },
    data: preferences,
  })

  return { rememberFilters: response.data?.rememberFilters ?? preferences.rememberFilters }
}

/**
 * Users who cannot sign in: deleted, locked out, or both.
 */
export const getInactiveEmployees = async (): Promise<inactiveEmployee[]> => {
  return (await GetJsonData('Employee/Inactive')) as inactiveEmployee[]
}

/**
 * Brings back a deleted or locked out user: clears the deleted date and the
 * sign in lockout.
 */
export const restoreEmployee = async (employeeId: number) => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.post(
    `https://${apiURL}/Employee/${employeeId}/Restore`,
    null,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    }
  )

  return response.data
}
